@let form = formData();
@let isDesktop = deviceService.isDesktop$ | async;
@let photoField = inputPhoto();
@let photoPreviewFieldKey = photoPreviewKey();

@if (
  photoPreviewFieldKey !== null && photoPreviewFieldKey === $photoTargetKey()
) {
  <x-input-photos-preview
    [field]="photoField"
    [target]="photoField ? $photoTargets()[photoField.key] : null"
    [canExecuteWorkflow]="$canExecuteWorkflow()"
    class="workflow-form max-w-[600px]"
    (photoSelected)="onPhotoSelected($event)"
    (photosSync)="photoSync()"
  />
} @else {
  <form
    class="workflow-form"
    [formGroup]="$workflowForm()"
    xFormChangeTracker
    #formChangeTracker="xFormChangeTracker"
    (ngSubmit)="sync()"
    [ngClass]="{
      'max-w-[600px]': isDesktop,
    }"
  >
    <!-- Main Section -->

    <tui-scrollbar class="flex-1 min-h-0">
      <div class="workflow-form-main">
        <!-- Form Title -->
        @if (form.title) {
          <h2 class="workflow-title text-[28px]/[32px] font-extrabold py-4">
            {{ form.title }}
          </h2>
        }

        @if (!$canExecuteWorkflow()) {
          <div
            class="mb-4 flex items-center gap-2 rounded-xl border border-(--tui-border-normal) bg-(--tui-background-base-alt) px-4 py-3 text-base x-text-yellow"
          >
            <tui-icon icon="@tui.circle-alert" class="shrink-0" />
            <span>{{
              'workflows.preview_other_executor_read_only' | translate
            }}</span>
          </div>
        }

        @for (group of mainGroups(); track group.order) {
          <ng-container
            *ngTemplateOutlet="groupTemplate; context: { $implicit: group }"
          ></ng-container>
        }
      </div>
    </tui-scrollbar>

    <!-- Footer Section -->
    <div class="workflow-form-footer">
      @for (group of footerGroups(); track group.order) {
        <ng-container
          *ngTemplateOutlet="groupTemplate; context: { $implicit: group }"
        ></ng-container>
      }
      @if ($dealDetailsSaveError(); as dealDetailsError) {
        <div
          class="mt-4 rounded-lg px-3 py-2 typo-body-s"
          role="alert"
          style="
            background: var(--tui-status-negative-pale);
            color: var(--tui-text-negative);
          "
        >
          {{ 'workflows.deal_details_not_saved' | translate }}:
          {{ dealDetailsError }}
        </div>
      }
      <button
        tuiButton
        type="submit"
        size="m"
        class="workflow-btn mt-4 w-full"
        [disabled]="$isSubmitting()"
      >
        @if (!$canExecuteWorkflow()) {
          {{ 'workflows.preview_no_rights' | translate }}
        } @else if ($workflowIsCompleted()) {
          {{ 'workflows.preview_sent' | translate }}
        } @else {
          {{ 'workflows.sync' | translate }}
        }
        @if (formChangeTracker.$isFormChanged()) {
          <tui-icon class="ml-1" icon="@tui.save"></tui-icon>
        }
      </button>
    </div>
  </form>
}

<!-- Group Template -->
<ng-template #groupTemplate let-group>
  <div
    class="mb-12"
    [class]="
      group.section !== 'footer' ? 'last-of-type:mb-11' : 'last-of-type:mb-0'
    "
  >
    <!-- Group Title and Description -->
    @if (group.title) {
      <h3 class="workflow-title text-[28px]/[32px] font-extrabold py-4">
        {{ group.title }}
      </h3>
    }
    @if (group.description) {
      <p class="workflow-title x-font-body-3 mb-4">{{ group.description }}</p>
    }

    <!-- Render all fields in the group -->
    <div class="space-y-4">
      @for (field of $any(group.fields); track field.order) {
        <!-- INPUT FIELDS -->
        @if (field.type === 'inputString') {
          <x-input-string-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Number Field -->
        @if (field.type === 'inputNumber') {
          <x-input-number-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Checkbox Field -->
        @if (field.type === 'inputCheckbox') {
          <x-input-checkbox-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Date Field -->
        @if (field.type === 'inputDate') {
          <x-input-date-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Datetime Field -->
        @if (field.type === 'inputDatetime') {
          <x-input-datetime-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Radio Field -->
        @if (field.type === 'inputRadio') {
          <x-input-radio-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Select Field -->
        @if (field.type === 'inputSelect') {
          <x-input-select-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Multiselect Field -->
        @if (field.type === 'inputMultiselect') {
          <x-input-multiselect-field
            [field]="field"
            [control]="getControl(field.key)"
          />
        }

        <!-- Input Files Field -->
        @if (field.type === 'inputFiles') {
          <x-input-files-field
            [field]="field"
            [control]="getControl(field.key)"
            [failedFiles]="$fileFailures()"
          />
        }

        <!-- Input Photos Field -->
        @if (field.type === 'inputPhotos') {
          <x-input-photos-field
            [field]="field"
            [target]="$photoTargets()[field.key]"
            [control]="getControl(field.key)"
            [canExecuteWorkflow]="$canExecuteWorkflow()"
            (photoSelected)="onPhotoSelected($event)"
          />
        }

        <!-- Input Actual Address Field -->
        @if (field.type === 'inputActualAddress') {
          <x-input-actual-address-field
            [field]="field"
            [control]="getControl(field.key)"
            [canExecute]="$canExecuteWorkflow()"
          />
        }

        <!-- Input DealDetails Field -->
        @if (field.type === 'inputDealDetails') {
          <x-input-deal-details-field
            [field]="field"
            [control]="getControl(field.key)"
            [canExecute]="$canExecuteWorkflow()"
          />
        }

        <!-- Input Button Field -->
        @if (field.type === 'inputButton') {
          <x-input-button-field
            [field]="field"
            [control]="getControl(field.key)"
            [canExecute]="$canExecuteWorkflow()"
          />
        }

        <!-- Input Devices Field -->
        @if (field.type === 'inputDevices') {
          <x-input-devices
            [field]="field"
            [control]="getControl(field.key)"
            [canExecute]="$canExecuteWorkflow()"
          />
        }

        <!-- Input Mistakes Field -->
        @if (field.type === 'inputMistakes') {
          <x-input-mistakes-field
            [field]="field"
            [control]="getControl(field.key)"
            [request]="request()"
            [workflowId]="workflowId()"
            [canExecute]="$canSetRequestMistakes()"
          />
        }

        <!-- OUTPUT FIELDS -->
        @if (field.type === 'outputText') {
          <x-output-text-field [field]="$any(field)" />
        }
        @if (field.type === 'outputRequestField') {
          <x-output-request-field [field]="$any(field)" />
        }
        @if (field.type === 'outputRequestFileCats') {
          <x-output-request-file-cats
            [field]="$any(field)"
            [request]="request()"
          />
        }
        @if (field.type === 'outputHint') {
          <x-output-hint-field [field]="$any(field)" />
        }
        @if (field.type === 'outputRequestComments') {
          <x-output-request-comments [field]="$any(field)" />
        }
        @if (field.type === 'outputFiles') {
          <x-output-files-field [field]="$any(field)" />
        }
        @if (field.type === 'outputLink') {
          <x-output-link-field [field]="$any(field)" />
        }
      }
    </div>
  </div>
</ng-template>
